import {
  addDisplayName,
  addProps,
  addHandlers,
  flowMax,
  addState,
} from 'ad-hok'
import { FC } from 'react'
import {
  FeedMedia,
  isImageFeedContent,
  videoThumbnail,
} from 'shared/src/api/types'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'
import {
  addNavigate,
  addOptionalIntPathParam,
} from 'shared/src/utils/addNavigate'
import { getEnvVar } from 'shared/src/utils/env'
import { addUser } from 'shared/src/api'
import { formatDate, formattedStat } from 'shared/src/utils/formatting'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import Icon from '../Icon'
import { Product } from './Product'
import { LABELS_STYLE, NOTES_MEDIUM_STYLE, BUTTONS_STYLE } from '../styles'
import { makeEditPostRoute } from '../../utils/routes'
import { MY_RODEO_PRODUCTS_Z_INDEX } from '../../utils/zIndex'
import { addToastContext } from '../toastContext'
import { ShareMediaModal } from './ShareMediaModal'
import shareIcon from '../../assets/share-icon.svg'

export const writeToClipboard = async (url: string) => {
  navigator.clipboard
    .writeText(url)
    .catch((error) => console.log('Error sharing:', error))
}

const useStyles = createUseStyles({
  row: {
    display: 'flex',
    gap: 18,
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    gap: 22,
    flexShrink: 1,
    flexGrow: 1,
  },
  right: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    flexGrow: 0,
    width: '50%',
  },
  shareContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  shareIcons: {
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'flex-end',
    gap: 20,
  },
  image: {
    aspectRatio: '9 / 16',
    flexShrink: 0,
    flexGrow: 0,
    background: '#2a2e31',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  label: {
    ...LABELS_STYLE,
    color: 'rgba(54, 52, 50, 0.56)',
  },
  value: {
    ...NOTES_MEDIUM_STYLE,
    color: 'rgba(54, 52, 50, 0.8)',
    marginBottom: 12,
  },
  caption: {
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  products: {
    display: 'flex',
    alignSelf: 'flex-end',
    zIndex: MY_RODEO_PRODUCTS_Z_INDEX,
    marginTop: 'auto',
  },
  product: {
    flexShrink: 0,
    marginLeft: -76,
  },
  editButton: {
    ...BUTTONS_STYLE,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#90734F',
    height: 48,
    paddingLeft: 2,
  },
  copyUrlIcon: {
    color: '#90734F',
  },
})

interface Props {
  media: FeedMedia
  showEditButton: boolean
}

export const Media: FC<Props> = flowMax(
  addDisplayName('MediaItem'),
  addTranslationHelpers,
  addProps(({ media }) => ({
    thumbnailUrl: media.coverImage
      ? media.coverImage.thumbnail
      : isImageFeedContent(media.content)
      ? media.content.value.thumbnail
      : videoThumbnail(media.content),
    createdAtFormatted: formatDate(media.createdAt),
  })),
  addNavigate,
  addUser,
  addOptionalIntPathParam('userId'),
  addToastContext,
  addHandlers({
    onEdit:
      ({ media: { id }, userId, navigate }) =>
      () => {
        navigate(makeEditPostRoute(id, userId))
      },
    onCopyUrl:
      ({
        media: {
          id,
          user: { username },
        },
        showSuccessToast,
        t,
      }) =>
      () => {
        const url = [
          getEnvVar('SHOPPER_URL'),
          username,
          'posts/rodeo#' + id,
        ].join('/')
        writeToClipboard(url).then(() =>
          showSuccessToast({
            title: t('cmsShareAlert'),
            details: url,
          })
        )
      },
  }),
  addProps(({ media: { numViews } }) => ({
    numViews: formattedStat(numViews),
  })),
  addState('shareMediaModalOpen', 'setShareMediaModalOpen', false),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    media,
    showEditButton,
    numViews,
    thumbnailUrl,
    createdAtFormatted,
    onEdit,
    onCopyUrl,
    shareMediaModalOpen,
    setShareMediaModalOpen,
    classes,
    t,
  }) => (
    <div>
      <div className={classes.row}>
        <div className={classes.left}>
          <div className={classes.image}>
            <img src={thumbnailUrl} alt="" />
          </div>
        </div>
        <div className={classes.right}>
          <div className={classes.shareContainer}>
            <p className={classes.label}>{t('admin.myRodeo.createdAt')}</p>
            {media.state === 'published' && (
              <div className={classes.shareIcons}>
                <button onClick={onCopyUrl}>
                  <Icon name="copy" className={classes.copyUrlIcon} />
                </button>
                <button onClick={() => setShareMediaModalOpen(true)}>
                  <img src={shareIcon} alt="" />
                </button>
              </div>
            )}
          </div>
          <p className={classes.value}>{createdAtFormatted}</p>
          <p className={classes.label}>{t('admin.myRodeo.caption')}</p>
          <p className={cx(classes.value, classes.caption)}>{media.caption}</p>
          <p className={classes.label}>{t('admin.myRodeo.views')}</p>
          <p className={cx(classes.value, classes.caption)}>{numViews}</p>
          <div className={classes.products}>
            {media.products.map((product) => (
              <Product
                product={product}
                className={classes.product}
                key={product.id}
              />
            ))}
          </div>
        </div>
      </div>
      {showEditButton && (
        <button onClick={onEdit} className={classes.editButton}>
          <Icon name="editFlipped" />
          {t('admin.myRodeo.editPost')}
        </button>
      )}
      {shareMediaModalOpen && (
        <ShareMediaModal
          imageUrl={thumbnailUrl}
          displayText={media.user.username ?? ''}
          onClose={() => setShareMediaModalOpen(false)}
        />
      )}
    </div>
  )
)
